import React, { useEffect, useState } from 'react';
import { MoreItemsLink, NamesList, PanelSectionContent, PanelSubHeader } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import { useStowageTankPlanStore } from 'src/context/StowageTankPlanContext';
import { useRotationStore } from 'src/context/RotationContext';
import { getCargoContentInTanksList } from 'src/utils/stowage/reportUtils';
import { getChartererName } from 'src/utils/stowage/cargoOperationUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { SelectOption } from 'src/types/ViewModel/SelectOption';
import OdfjellInputComponent, { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import styled from 'styled-components';
import Tooltip from 'src/components/ui/tooltip/Tooltip';
import { SelectedCharterersList } from './reportPanel.style';

const ClearButton = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.text.secondary};
    cursor: pointer;
    padding: 4px 8px;
    display: flex;
    align-items: center;
    gap: 4px;

    &:hover {
        background-color: ${({ theme }) => theme.colors.background.lightGrey};
        border: none;
    }

    &:disabled {
        cursor: default;
        opacity: 0.5;
    }
`;

interface ReportPanelChartererSelectorProps {
    selectedCharterers: string[];
    setSelectedCharterers: (charterers: string[]) => void;
}

const ReportPanelChartererSelector: React.FC<ReportPanelChartererSelectorProps> = ({ selectedCharterers, setSelectedCharterers }) => {
    const [chartererOptions, setChartererOptions] = useState<SelectOption[]>([]);
    const { activeStowagePlan } = useStowagePlanStore();
    const { tankPlan } = useStowageTankPlanStore();
    const { selectedStowageRotationPortCall, selectedStowageRotationBerthCall } = useRotationStore();

    // Fetch and process charterer options
    useEffect(() => {
        if (activeStowagePlan && tankPlan && selectedStowageRotationPortCall) {
            // Get all cargo contents from the stowage plan for the current port/berth
            const _cargoContents = getCargoContentInTanksList(
                tankPlan,
                activeStowagePlan,
                selectedStowageRotationPortCall,
                selectedStowageRotationBerthCall,
                null
            );

            // Extract unique charterer names
            const _charterersSet = new Set<string>();

            _cargoContents.forEach((_content) => {
                const chartererName = getChartererName(_content.cargo);
                if (chartererName) {
                    _charterersSet.add(chartererName);
                }
            });

            // Convert to options array for the select component
            const _options = Array.from(_charterersSet)
                .filter(
                    (_charterer) =>
                        _charterer && _charterer.trim() !== '' && selectedCharterers.indexOf(_charterer) === -1 && _charterer !== 'Not assigned'
                )
                .sort()
                .map((_charterer) => new SelectOption(_charterer, _charterer));

            setChartererOptions(_options);
        }
    }, [activeStowagePlan, tankPlan, selectedStowageRotationPortCall, selectedStowageRotationBerthCall, selectedCharterers]);

    // Handle charterer selection changes
    const _handleChartererSelection = (changeObject: InputChangeData) => {
        console.log(changeObject.event.target.value);
        setSelectedCharterers([...selectedCharterers, changeObject.event.target.value]);
    };

    // Clear all selected charterers
    const _handleClearSelection = () => {
        setSelectedCharterers([]);
    };

    const _isSelectionDisabled = chartererOptions.length === 0;
    const _hasSelectedCharterers = selectedCharterers.length > 0;

    return (
        <PanelSectionContent>
            <PanelSubHeader noBorder>Charterer(s)</PanelSubHeader>

            <OdfjellInputComponent
                type="select"
                options={chartererOptions}
                selectDefaultOption="Select charterer(s)..."
                onChange={_handleChartererSelection}
                value="Select charterer(s)..."
                isReadOnly={_isSelectionDisabled}
                textAlign="left"
            />

            <SelectedCharterersList>
                {selectedCharterers.length > 0 && (
                    <div>
                        {(() => {
                            const MAX_VISIBLE_CHARTERERS = 3;
                            const _visibleCharterers = selectedCharterers.slice(0, MAX_VISIBLE_CHARTERERS);
                            const _remainingCharterers = selectedCharterers.slice(MAX_VISIBLE_CHARTERERS);
                            const _hasMoreCharterers = _remainingCharterers.length > 0;

                            const renderRemainingCharterersTooltip = () => {
                                return <div>{_remainingCharterers.join(', ')}</div>;
                            };

                            return (
                                <NamesList>
                                    {_visibleCharterers.map((charterer, index) => (
                                        <div key={index}>
                                            {charterer}
                                            {index < _visibleCharterers.length - 1 || _hasMoreCharterers ? ',' : ''}
                                        </div>
                                    ))}

                                    {_hasMoreCharterers && (
                                        <Tooltip text={renderRemainingCharterersTooltip()} type="information">
                                            <MoreItemsLink>and {_remainingCharterers.length} more...</MoreItemsLink>
                                        </Tooltip>
                                    )}
                                </NamesList>
                            );
                        })()}
                    </div>
                )}

                <ClearButton onClick={_handleClearSelection} disabled={!_hasSelectedCharterers}>
                    <FontAwesomeIcon icon={faTimes} />
                    Clear
                </ClearButton>
            </SelectedCharterersList>
        </PanelSectionContent>
    );
};

export default ReportPanelChartererSelector;
